import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, Calendar, Users, TrendingUp, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface ModalConfig {
  enabled: boolean;
  frequencyDays: number;
  profileCount: number;
  showDelay: number;
}

export function DailyInterestModalSettings() {
  const [config, setConfig] = useState<ModalConfig>({
    enabled: true,
    frequencyDays: 3,
    profileCount: 15,
    showDelay: 2000
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/daily-interest-modal-config/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setConfig(response.data.data);
        setLastUpdated(response.data.data.updatedAt || '');
      }
    } catch (error) {
      // Use defaults if not found
      toast.info('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${API_URL}/daily-interest-modal-config/config`,
        config,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Settings saved successfully!');
        setLastUpdated(new Date().toISOString());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-rose-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-rose-100 to-pink-100 rounded-xl">
            <Sparkles className="size-6 text-rose-600" />
          </div>
          <div>
            <CardTitle>Daily Interest Modal Settings</CardTitle>
            <CardDescription>
              Configure automatic match suggestion modal that appears to users
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Badge */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
          <div className="flex items-center gap-2">
            {config.enabled ? (
              <>
                <CheckCircle className="size-5 text-green-600" />
                <span className="font-medium text-green-700">Modal is Active</span>
              </>
            ) : (
              <>
                <AlertCircle className="size-5 text-amber-600" />
                <span className="font-medium text-amber-700">Modal is Disabled</span>
              </>
            )}
          </div>
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </span>
          )}
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-white border rounded-xl">
          <div className="flex items-center gap-3">
            <div className="bg-purple-50 p-2 rounded-lg">
              <Sparkles className="size-5 text-purple-600" />
            </div>
            <div>
              <Label className="text-base font-semibold">Enable Modal</Label>
              <p className="text-sm text-gray-500">Show daily match suggestions to users</p>
            </div>
          </div>
          <Switch 
            checked={config.enabled}
            onCheckedChange={(value) => setConfig({ ...config, enabled: value })}
          />
        </div>

        {/* Frequency Days */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="size-5 text-blue-600" />
            <Label htmlFor="frequencyDays" className="font-semibold">
              Frequency (Days)
            </Label>
          </div>
          <Input
            id="frequencyDays"
            type="number"
            min="1"
            max="30"
            value={config.frequencyDays}
            onChange={(e) => setConfig({ ...config, frequencyDays: parseInt(e.target.value) || 3 })}
            className="max-w-xs"
          />
          <p className="text-sm text-gray-500">
            Modal will appear every <strong>{config.frequencyDays} days</strong> per user. 
            If user closes it, they won't see it again until {config.frequencyDays} days have passed.
          </p>
          <div className="flex gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              Recommended: 3-5 days
            </Badge>
            <Badge variant="outline" className="text-xs">
              Current: {config.frequencyDays} {config.frequencyDays === 1 ? 'day' : 'days'}
            </Badge>
          </div>
        </div>

        {/* Profile Count */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Users className="size-5 text-green-600" />
            <Label htmlFor="profileCount" className="font-semibold">
              Number of Profiles
            </Label>
          </div>
          <Input
            id="profileCount"
            type="number"
            min="5"
            max="20"
            value={config.profileCount}
            onChange={(e) => setConfig({ ...config, profileCount: parseInt(e.target.value) || 15 })}
            className="max-w-xs"
          />
          <p className="text-sm text-gray-500">
            Show <strong>{config.profileCount} suggested profiles</strong> in the modal
          </p>
          <div className="flex gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              Recommended: 10-15 profiles
            </Badge>
            <Badge variant="outline" className="text-xs">
              Current: {config.profileCount} profiles
            </Badge>
          </div>
        </div>

        {/* Show Delay */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="size-5 text-amber-600" />
            <Label htmlFor="showDelay" className="font-semibold">
              Show Delay (milliseconds)
            </Label>
          </div>
          <Input
            id="showDelay"
            type="number"
            min="500"
            max="10000"
            step="500"
            value={config.showDelay}
            onChange={(e) => setConfig({ ...config, showDelay: parseInt(e.target.value) || 2000 })}
            className="max-w-xs"
          />
          <p className="text-sm text-gray-500">
            Wait <strong>{config.showDelay / 1000} seconds</strong> after page load before showing modal
          </p>
          <div className="flex gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              Recommended: 2000ms (2 seconds)
            </Badge>
            <Badge variant="outline" className="text-xs">
              Current: {config.showDelay}ms ({config.showDelay / 1000}s)
            </Badge>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">How it Works:</h4>
          <ul className="space-y-1 text-sm text-blue-700">
            <li>• Modal shows to users every {config.frequencyDays} days automatically</li>
            <li>• Users can click "Don't show again" to permanently disable it</li>
            <li>• Modal displays {config.profileCount} high-match profiles</li>
            <li>• Users can send interests directly from the modal</li>
            <li>• If disabled here, modal won't show to any user</li>
          </ul>
        </div>

        {/* Stats Preview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl border border-rose-200">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="size-4 text-rose-600" />
              <span className="text-xs font-medium text-rose-700 uppercase">Frequency</span>
            </div>
            <p className="text-2xl font-bold text-rose-900">{config.frequencyDays}</p>
            <p className="text-xs text-rose-600 mt-1">Days between shows</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="size-4 text-green-600" />
              <span className="text-xs font-medium text-green-700 uppercase">Profiles</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{config.profileCount}</p>
            <p className="text-xs text-green-600 mt-1">Suggestions shown</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="size-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700 uppercase">Delay</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{config.showDelay / 1000}s</p>
            <p className="text-xs text-blue-600 mt-1">Show after load</p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between items-center bg-gray-50 border-t">
        <p className="text-sm text-gray-500">
          Changes will apply to all users immediately
        </p>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="size-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="size-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}